/**
 * Story 31.5 — het rekenwerk achter de kleur en het verschil.
 *
 * Hier staat uitsluitend rekenwerk en geen opmaak, om dezelfde reden als bij `kerncijfers.ts`: de
 * scherpste regels van deze story zijn rekenregels, en die horen navraagbaar op één plek te staan
 * in plaats van verspreid over drie onderdelen die er elk hun eigen versie van maken.
 *
 * Twee grenzen en één afwezigheid bepalen alles:
 *
 * - PRECIES OP DE NORM is groen. "Onder de norm" is inclusief.
 * - PRECIES TIEN PROCENT ERBOVEN is amber. Rood begint BÓVEN die tien procent.
 * - GEEN NORM is geen nul. Zonder norm valt er niets te oordelen en niets te verschillen.
 *
 * Allebei de grenzen worden AFGEROND beslecht; `nietGroterDan` hieronder legt uit waarom een
 * vergelijking zonder afronding per norm een haartje verschuift.
 */

/** Eén norm per stap, in minuten per stuk. `null` betekent "geen norm gezet", nooit de waarde nul. */
export interface Normen {
  vastleggen: number | null;
  valideren: number | null;
}

/**
 * De drie standen, plus de vierde die er geen is.
 *
 * `geen` is nadrukkelijk een eigen stand en geen stilzwijgend groen: zonder norm, of zonder meting,
 * is er niets beoordeeld. Een onderdeel dat daar toch een kleur op plakt zegt iets wat niemand
 * heeft uitgesproken.
 */
export type Oordeel = 'groen' | 'amber' | 'rood' | 'geen';

/** De marge waarbinnen amber loopt: tot en met tien procent boven de norm. */
const AMBER_MARGE = 1.1;

/**
 * Het aantal decimalen waarop een vergelijking met de norm beslecht wordt.
 *
 * Zes is ruim voorbij alles wat een behandeltijd betekenis geeft — minuten per stuk worden op één
 * decimaal getoond — en ruim binnen wat drijvende komma aan ruis maakt.
 */
const PRECISIE = 1e6;

/**
 * Twee getallen vergelijken zoals een lezer ze zou vergelijken.
 *
 * Beide kanten worden op hetzelfde aantal decimalen afgerond en daarna als HELE getallen naast
 * elkaar gelegd; zo verdwijnt de ruis van de drijvende komma in plaats van van plek te veranderen.
 *
 * Dit is de tweede poging, en de eerste staat hier als waarschuwing. Vergelijken in tienden —
 * `waarde * 10 <= norm * 11` — lost 40 en 8 op maar VERPLAATST de val alleen: bij een norm van 12,7
 * geeft `13.97 * 10` de waarde 139.70000000000002 tegenover `12.7 * 11` = 139.7, en dan kleurt
 * precies-op-de-grens rood. Ook 0,7 en 5,6 vallen zo verkeerd uit. Elke vermenigvuldiging heeft
 * zulke normen; alleen afronden vóór de vergelijking haalt ze weg.
 */
function nietGroterDan(waarde: number, grens: number): boolean {
  return Math.round(waarde * PRECISIE) <= Math.round(grens * PRECISIE);
}

/**
 * Het oordeel van één gemeten waarde tegen de norm van díe stap.
 *
 * Allebei de grenzen zijn INCLUSIEF en allebei worden ze afgerond beslecht: precies op de norm is
 * groen, precies tien procent erboven is amber. Het gemiddelde per stuk is zelf een deling en
 * draagt dus zijn eigen ruis — zonder die afronding zou 4,1 tegen een norm van 4,1 amber kunnen
 * kleuren omdat de deling op 4,1000000000000005 uitkwam.
 */
export function oordeelVoorNorm(waarde: number | null, norm: number | null): Oordeel {
  // Een norm van nul is geen norm maar een fout; hij komt de serverlaag niet door en wordt hier
  // dus ook niet als norm behandeld.
  if (waarde === null || norm === null || !(norm > 0)) return 'geen';
  if (nietGroterDan(waarde, norm)) return 'groen';
  if (nietGroterDan(waarde, norm * AMBER_MARGE)) return 'amber';
  return 'rood';
}

/**
 * Hoeveel de gemeten waarde van de norm afligt, in minuten per stuk.
 *
 * `gelijk` staat los van `boven` met nul erbij: precies op de norm is een eigen uitkomst, geen
 * overschrijding van niets.
 */
export interface VerschilMetNorm {
  soort: 'boven' | 'onder' | 'gelijk' | 'geen';
  /** Altijd positief, of `null` zodra er niets te verschillen valt. De soort zegt welke kant op. */
  minuten: number | null;
}

export function verschilMetNorm(waarde: number | null, norm: number | null): VerschilMetNorm {
  if (waarde === null || norm === null || !(norm > 0)) return { soort: 'geen', minuten: null };
  const verschil = waarde - norm;
  if (verschil === 0) return { soort: 'gelijk', minuten: 0 };
  return verschil > 0 ? { soort: 'boven', minuten: verschil } : { soort: 'onder', minuten: -verschil };
}
